// Fetches a page's Visual Editor sections and renders them via the shared
// SectionRenderer. When the admin hasn't saved any sections yet, an optional
// `defaults` prop can seed the render so pages never appear blank.
//
// Two ways to pass content:
//   - `pageKey`  — fetches from /site-settings/page-sections/{pageKey}
//   - `sections` — pre-loaded sections (e.g. per-pathway intros)
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SectionRenderer from "@/components/SectionRenderer";
import { adaptLegacyPageSections } from "@/components/sectionTypes";

export default function PageSections({ pageKey, sections: propSections, defaults, className = "" }) {
  const [sections, setSections] = useState(propSections !== undefined ? (propSections || []) : null);
  const [veSettings, setVeSettings] = useState(null);

  useEffect(() => {
    if (propSections !== undefined) { setSections(propSections || []); return; }
    if (!pageKey) return;
    api.get(`/site-settings/page-sections/${pageKey}`)
       .then(r => setSections(r.data.sections || []))
       .catch(() => setSections([]));
  }, [pageKey, propSections]);

  useEffect(() => {
    api.get("/site-settings/visual-editor").then(r => setVeSettings(r.data)).catch(() => setVeSettings({}));
  }, []);

  if (sections === null) return null;
  // Render-only compatibility: map any legacy pre-subtype section records to
  // the current dynamic schema before filtering/rendering (no DB mutation).
  const adapted = adaptLegacyPageSections(sections);
  const visibleSaved = adapted.filter(s => !s.hidden);
  const effective = visibleSaved.length ? visibleSaved : (adapted.length ? [] : (defaults || []).filter(s => !s.hidden));
  if (effective.length === 0) return null;

  return (
    <div data-testid={pageKey ? `page-sections-${pageKey}` : "page-sections-inline"}
         className={className}>
      {effective.map((s, i) => {
        const isDynamic = s?.type === "dynamic";
        return isDynamic ? (
          <SectionRenderer key={s.id || i} section={s} veSettings={veSettings}/>
        ) : (
          <div key={s.id || i} className="blog-content">
            <SectionRenderer section={s} veSettings={veSettings}/>
          </div>
        );
      })}
    </div>
  );
}
